import React, { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import AuditDoc, { docPages } from "@/components/site/AuditDoc";
import { pauseTracking } from "@/lib/track";

// A drafted Audit, shown to us exactly as the client will see it, before it
// is published. The link is made by a tool and runs out after an hour; what
// opens it rides after "#", which browsers never send to a server.
export default function AuditPreview() {
  const [state, setState] = useState({ phase: "loading" });

  // Ours alone: never measured or recorded.
  useEffect(() => pauseTracking(), []);
  useEffect(() => {
    document.title = "Audit preview | Nomus";
  }, [state.phase]);
  useEffect(() => {
    const [slug, until, seal] = window.location.hash.slice(1).split(".");
    if (!slug || !until || !seal) return setState({ phase: "gone" });
    let alive = true;
    fetch("/api/room/draft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, until: Number(until), seal }),
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!alive) return;
        setState(r.ok ? { phase: "ready", ...body } : { phase: "gone" });
      })
      .catch(() => alive && setState({ phase: "gone" }));
    return () => {
      alive = false;
    };
  }, []);

  if (state.phase !== "ready")
    return (
      <div className="flex min-h-[70vh] items-center px-5 pt-24">
        <p className="mx-auto max-w-[520px] text-center text-[19px] leading-[1.4] text-black/60">
          {state.phase === "loading"
            ? "One moment."
            : "This preview link has run out, or was not copied whole. Ask for a new one."}
        </p>
      </div>
    );

  const { doc, client, missing, left_out: leftOut } = state;
  const draft = state.state === "draft";
  return (
    <div data-testid="audit-preview">
      <section className="bg-black px-5 pb-8 pt-24 text-white sm:pt-28 print:hidden">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-end justify-between gap-6">
          <div className="max-w-[760px]">
            <p className="text-[11px] uppercase tracking-[0.1em] text-white/50">
              {draft ? "Draft · only we can see this" : "As published"}
            </p>
            <h1 className="nm-title mt-3 text-[36px] leading-[1] sm:text-[56px]">
              The Audit for {client}
            </h1>
            <p className="mt-4 text-[16px] leading-[1.45] text-white/65">
              {draft
                ? "This is what they will read once it is published, and what Save as PDF will give them. Nothing here is on their page yet."
                : "This is on their page now. No changes are drafted."}
            </p>
            {missing.length > 0 && (
              <p className="mt-4 text-[15px] text-red-300" role="alert">
                Cannot be published yet. Missing: {missing.join(", ")}.
              </p>
            )}
            {leftOut.length > 0 && (
              <p className="mt-2 text-[14px] text-white/50">
                Pages left out: {leftOut.join(", ")}.
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[13px] text-white/50">
              {docPages(doc).length} pages
            </span>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex h-11 items-center gap-2 rounded-full px-6 text-[13px] font-medium text-white ring-1 ring-white/30 transition-colors hover:bg-white/10"
            >
              <Printer size={14} /> Save as PDF
            </button>
          </div>
        </div>
      </section>
      <AuditDoc doc={doc} client={client} />
    </div>
  );
}
